import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Calendar, MapPin, Plus, X, Clock } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = {
  laboratorio: "Laboratorio",
  incontro: "Incontro",
  ritiro: "Ritiro",
  festa: "Festa",
  formazione: "Formazione",
};

const CATEGORIES_PLURAL = {
  laboratorio: "Laboratori",
  incontro: "Incontri",
  ritiro: "Ritiri",
  festa: "Feste",
  formazione: "Formazioni",
};

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
};

export default function Events() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [showForm, setShowForm] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    const r = await axios.get(`${API}/events`, { params: cat ? { category: cat } : {} });
    setItems(r.data);
  };

  useEffect(() => { load(); }, [cat]);

  const myPending = items.filter(e => e.status === "pending");

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="events-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-tc-secondary mb-3">Si incontrano</div>
          <h1 className="font-serif text-5xl md:text-6xl text-tc-primary">Eventi & laboratori</h1>
          <p className="text-tc-muted mt-4 max-w-xl">
            Ritiri nel bosco, cerchi di condivisione, festival della terra, corsi di permacultura. Un cammino fatto di incontri.
          </p>
        </div>
        {user && (
          <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2 self-start" data-testid="add-event-btn">
            <Plus className="w-4 h-4" /> Proponi un evento
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="event-filters">
        <Chip active={!cat} onClick={() => setCat("")} label="Tutti" />
        {Object.entries(CATEGORIES_PLURAL).map(([k, v]) => (
          <Chip key={k} active={cat === k} onClick={() => setCat(k)} label={v} />
        ))}
      </div>

      {myPending.length > 0 && (
        <div className="bg-tc-secondary/10 border border-tc-secondary/30 rounded-2xl p-4 mb-8 text-sm text-tc-secondary flex items-center gap-2" data-testid="pending-events-banner">
          <Clock className="w-4 h-4" /> Hai {myPending.length} {myPending.length === 1 ? 'evento' : 'eventi'} in attesa di approvazione
        </div>
      )}

      <div className="grid md:grid-cols-12 gap-6 auto-rows-[200px]">
        {items.map((e, i) => (
          <EventCard key={e.id} event={e} size={patternFor(i)} />
        ))}
      </div>

      {showForm && <AddEventModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </main>
  );
}

function patternFor(i) {
  const patterns = [
    "md:col-span-8 md:row-span-2",
    "md:col-span-4 md:row-span-1",
    "md:col-span-4 md:row-span-1",
    "md:col-span-6 md:row-span-1",
    "md:col-span-6 md:row-span-2",
    "md:col-span-6 md:row-span-1",
  ];
  return patterns[i % patterns.length];
}

function EventCard({ event, size }) {
  return (
    <article
      className={`relative overflow-hidden rounded-3xl border border-tc/60 bg-tc-surface p-6 flex flex-col justify-between group hover:-translate-y-1 transition-all ${size}`}
      style={event.image ? { backgroundImage: `linear-gradient(180deg, rgba(242,235,225,0.2) 0%, rgba(45,74,34,0.85) 100%), url(${event.image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
      data-testid={`event-${event.id}`}
    >
      <div className={event.image ? "text-tc-bg" : "text-tc-primary"}>
        <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest ${event.image ? 'text-tc-bg/90' : 'text-tc-secondary'} mb-3`}>
          <Calendar className="w-3 h-3" />
          {formatDate(event.date)}
        </div>
        <h3 className={`font-serif text-2xl md:text-3xl leading-tight ${event.image ? 'text-tc-bg' : 'text-tc-primary'}`}>{event.title}</h3>
        <p className={`text-sm mt-2 line-clamp-3 ${event.image ? 'text-tc-bg/90' : 'text-tc-muted'}`}>{event.description}</p>
      </div>
      <div className={`flex items-center justify-between text-xs mt-4 ${event.image ? 'text-tc-bg/80' : 'text-tc-muted'}`}>
        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {event.location}</span>
        <span className="uppercase tracking-widest">{CATEGORIES[event.category]}</span>
      </div>
    </article>
  );
}

function Chip({ active, onClick, label }) {
  return (
    <button onClick={onClick} data-testid={`ev-filter-${label}`}
      className={`px-5 py-2 rounded-full text-sm transition-colors border ${active ? 'bg-tc-primary text-tc-bg border-tc-primary' : 'text-tc-muted border-tc hover:border-tc-primary'}`}>
      {label}
    </button>
  );
}

function AddEventModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "", description: "", date: "", location: "", organizer: "", category: "laboratorio", image: ""
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/events`, form);
      toast.success("Evento inviato. Sarà visibile dopo l'approvazione.");
      onSaved();
    } catch {
      toast.error("Accedi per proporre un evento");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-tc-primary/40 backdrop-blur-sm z-40 flex items-center justify-center p-4" data-testid="add-event-modal">
      <div className="bg-tc-bg rounded-3xl max-w-xl w-full p-8 border border-tc relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-tc-muted hover:text-tc-primary" data-testid="close-event-modal"><X /></button>
        <h2 className="font-serif text-3xl text-tc-primary mb-6">Proponi un evento</h2>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Titolo"><input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="tc-input" data-testid="ev-title" /></Field>
          <Field label="Descrizione"><textarea required rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="tc-input resize-none" data-testid="ev-desc" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Data"><input type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="tc-input" data-testid="ev-date" /></Field>
            <Field label="Categoria">
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="tc-input" data-testid="ev-cat">
                {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Luogo"><input required value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="tc-input" data-testid="ev-loc" /></Field>
          <Field label="Organizzatore"><input required value={form.organizer} onChange={e => setForm({ ...form, organizer: e.target.value })} className="tc-input" data-testid="ev-org" /></Field>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="ev-submit">{busy ? "Invio…" : "Pubblica"}</button>
        </form>
        <style>{`.tc-input { width:100%; background:transparent; border:none; border-bottom:1px solid var(--tc-border); padding: 12px 0; outline:none; font-family:inherit; }
        .tc-input:focus { border-color: var(--tc-primary); }`}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-tc-muted block mb-1">{label}</label>
      {children}
    </div>
  );
}
